import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Session token (Phase 1 auth). Cookies carry the session on web (same-origin),
// but we also attach a Bearer token so mobile / cross-context calls authenticate.
const SESSION_TOKEN_KEY = 'dr_session_token';

// Add request interceptor to bust cache on GET requests and handle FormData
api.interceptors.request.use((config) => {
  if (config.method === 'get') {
    config.params = {
      ...config.params,
      _t: Date.now(), // Cache-busting timestamp
    };
  }
  
  // When sending FormData, delete the default Content-Type header
  // This allows the browser to set it correctly with the proper boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  // Attach the session token as a Bearer header when present.
  try {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (e) { /* localStorage unavailable */ }
  
  return config;
});

// Persist any session token the backend returns on login/signup, and send the
// user back to login when a session ACTUALLY expires. A single 401 no longer
// logs the user out: we verify once via /auth/me first, because iOS Safari can
// fire a racy request during bfcache restore (before the token is attached)
// that 401s even though the session is still valid.
let _sessionVerifyPromise = null;

api.interceptors.response.use(
  (response) => {
    const token = response?.data?.session_token;
    if (token) {
      try { localStorage.setItem(SESSION_TOKEN_KEY, token); } catch (e) { /* noop */ }
    }
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    // Aborted / network-error requests (tab was suspended) have NO response —
    // never treat those as an auth failure.
    if (status !== 401 || error?.config?.__skipAuthRetry) {
      return Promise.reject(error);
    }
    const path = window.location.pathname;
    const publicPaths = ['/login', '/signup', '/', '/pricing', '/membership', '/lender-draw-demo', '/lender-portal-demo', '/onboarding-vision', '/contractor-portal', '/contractor-compliance'];
    const isPublic = publicPaths.some((p) => path === p || path.startsWith(p + '/'));
    if (isPublic || window.__dr_authRedirecting) {
      return Promise.reject(error);
    }
    // Verify the session is REALLY dead before logging out.
    try {
      if (!_sessionVerifyPromise) {
        _sessionVerifyPromise = api.get('/auth/me', { __skipAuthRetry: true });
      }
      await _sessionVerifyPromise;
      _sessionVerifyPromise = null;
      // Session is still valid — the original 401 was transient/racy. Let the
      // caller (and useResumeRefresh) retry instead of booting the user.
      return Promise.reject(error);
    } catch (verifyErr) {
      _sessionVerifyPromise = null;
      // Session really is gone: clear ALL auth state and redirect once.
      try {
        localStorage.removeItem(SESSION_TOKEN_KEY);
        localStorage.removeItem('user');
      } catch (e) { /* noop */ }
      window.__dr_authRedirecting = true;
      window.location.replace('/login?expired=1');
      return Promise.reject(error);
    }
  }
);

export const apiService = {
  // Contact
  submitContactForm: (data) => api.post('/contact', data),
  
  // Auth
  signup: (data) => api.post('/auth/signup', data),
  logout: () => api.post('/auth/logout'),
  authMe: () => api.get('/auth/me'),
  signupWithPayment: (data) => api.post('/auth/signup-with-payment', data),
  signupFree: (data) => api.post('/auth/signup-free', data),
  recordSignupLead: (data) => api.post('/signup/lead', data),

  // Lender V2 demo (banks, roles, loans, access control)
  lenderDemoEntry: () => api.get('/lender/v2/demo-entry'),
  lenderContext: (actorId) => api.get('/lender/v2/context', { params: { actor_id: actorId } }),
  lenderLoans: (actorId) => api.get('/lender/v2/loans', { params: { actor_id: actorId } }),
  lenderBankUsers: (actorId) => api.get('/lender/v2/bank-users', { params: { actor_id: actorId } }),
  lenderAddBankUser: (actorId, data) => api.post('/lender/v2/bank-users', data, { params: { actor_id: actorId } }),
  lenderRemoveBankUser: (actorId, uid) => api.delete(`/lender/v2/bank-users/${uid}`, { params: { actor_id: actorId } }),
  lenderAssignLoan: (actorId, loanId, officerId) =>
    api.post(`/lender/v2/loans/${loanId}/assign`, { loan_officer_id: officerId }, { params: { actor_id: actorId } }),
  lenderDraws: (actorId, scope = 'all') => api.get('/lender/v2/draws', { params: { actor_id: actorId, scope } }),
  lenderDrawDetail: (actorId, drawId) => api.get(`/lender/v2/draws/${drawId}`, { params: { actor_id: actorId } }),
  lenderSendBack: (actorId, drawId, note) => api.post(`/lender/v2/draws/${drawId}/send-back`, { note }, { params: { actor_id: actorId } }),
  lenderApproveDraw: (actorId, drawId, note) => api.post(`/lender/v2/draws/${drawId}/approve`, { note }, { params: { actor_id: actorId } }),
  lenderBorrowerView: (loanId) => api.get(`/lender/v2/borrower/${loanId}`),
  lenderSubmitDraw: (loanId, data) => api.post(`/lender/v2/borrower/${loanId}/draws`, data),
  lenderResubmitDraw: (loanId, drawId, data) => api.post(`/lender/v2/borrower/${loanId}/draws/${drawId}/resubmit`, data),
  lenderResetDemo: () => api.post('/lender/v2/demo/reset'),
  lenderDemoProjects: () => api.get('/lender/v2/demo-projects'),
  lenderCreateApplication: (data) => api.post('/lender/v2/applications', data),
  lenderApplications: (actorId) => api.get('/lender/v2/applications', { params: { actor_id: actorId } }),
  lenderApplicationDetail: (appId, actorId) => api.get(`/lender/v2/applications/${appId}`, { params: actorId ? { actor_id: actorId } : {} }),
  lenderAppRequestDocs: (actorId, appId, data) => api.post(`/lender/v2/applications/${appId}/request-docs`, data, { params: { actor_id: actorId } }),
  lenderAppUploadDocs: (appId, data) => api.post(`/lender/v2/applications/${appId}/upload-docs`, data),
  lenderAppApprove: (actorId, appId, data) => api.post(`/lender/v2/applications/${appId}/approve`, data, { params: { actor_id: actorId } }),
  lenderAppClose: (actorId, appId, data) => api.post(`/lender/v2/applications/${appId}/close`, data, { params: { actor_id: actorId } }),
  lenderRequestAdditionalFunds: (loanId, data) => api.post(`/lender/v2/borrower/${loanId}/request-additional-funds`, data),
  activateFreeMembership: (data) => api.post('/membership/activate-free', data),
  getAddonCatalog: () => api.get('/addons/catalog'),
  getMyAddons: (userId) => api.get(`/addons/mine?user_id=${userId}`),
  addonCheckout: (data) => api.post('/addons/checkout', data),
  addonConfirm: (data) => api.post('/addons/confirm', data),
  login: (data) => api.post('/auth/login', data),
  verifyOtp: (email, code) => api.post('/auth/verify-otp', { email, code }),
  resendOtp: (email) => api.post('/auth/resend-otp', { email }),
  sendLenderAlertEmail: (data) => api.post('/demo/lender-alert-email', data),
  sendLenderDemoInvite: (data) => api.post('/demo/lender-demo-invite', data),
  getContractorPaymentReport: (userId, start, end) => api.get('/reports/contractor-payments', { params: { user_id: userId, start, end } }),
  adminLogin: (email, password) => api.post('/auth/admin-login', { email, password }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  updateProfile: (userId, data) => api.patch(`/auth/profile/${userId}`, data),
  changePassword: (userId, data) => api.post(`/auth/change-password/${userId}`, data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  deleteAccount: (userId, password, confirmation) => api.delete(`/auth/account/${userId}`, { data: { password, confirmation } }),
  teamMemberSignup: (data) => api.post('/auth/team-member-signup', data),
  
  // Stripe
  getStripePublishableKey: () => api.get('/stripe/publishable-key'),
  
  // Subscription Management
  getSubscriptionDetails: (userId) => api.get(`/subscription/details/${userId}`),
  createSubscriptionForUser: (userId, data) => api.post(`/subscription/create/${userId}`, data),
  changePlan: (userId, newTierId, billingCycle) => api.post(`/subscription/change-plan/${userId}`, { new_tier_id: newTierId, billing_cycle: billingCycle }),
  reactivateSubscription: (userId) => api.post(`/subscription/reactivate/${userId}`),
  createCustomerSetupIntent: (userId) => api.post(`/subscription/create-setup-intent/${userId}`),
  
  // Team Management
  inviteTeamMember: (ownerId, data) => api.post('/team/invite', data, { params: { owner_id: ownerId } }),
  verifyTeamInvite: (token) => api.get('/team/verify-invite', { params: { token } }),
  acceptTeamInvite: (token, userId) => api.post('/team/accept-invite', {}, { params: { token, user_id: userId } }),
  getTeamMembers: (ownerId) => api.get('/team/members', { params: { owner_id: ownerId } }),
  getMyTeamAccess: (userId) => api.get('/team/my-access', { params: { user_id: userId } }),
  removeTeamMember: (memberId, ownerId) => api.delete(`/team/members/${memberId}`, { params: { owner_id: ownerId } }),
  updateTeamMember: (memberId, ownerId, data) => api.patch(`/team/members/${memberId}`, data, { params: { owner_id: ownerId } }),
  getPendingInvites: (ownerId) => api.get('/team/pending-invites', { params: { owner_id: ownerId } }),
  cancelInvite: (inviteId, ownerId) => api.delete(`/team/invites/${inviteId}`, { params: { owner_id: ownerId } }),
  checkTeamAccess: (ownerId, userId) => api.get(`/team/check-access/${ownerId}`, { params: { user_id: userId } }),
  
  // Users
  createUser: (data) => api.post('/users', data),
  getUser: (userId) => api.get(`/users/${userId}`),

  // Projects
  createProject: (data) => api.post('/projects', data),
  getProjects: (userId) => api.get('/projects', { params: { user_id: userId } }),
  getDashboardOverview: (userId) => api.get('/dashboard/overview', { params: { user_id: userId } }),
  getDrawReadinessDetail: (projectId) => api.get(`/projects/${projectId}/draw-readiness-detail`),
  requestMigrationCall: (userId, data) => api.post('/migration-call-requests', data, { params: { user_id: userId } }),
  getProject: (projectId) => api.get(`/projects/${projectId}`),
  updateProject: (projectId, data) => api.patch(`/projects/${projectId}`, data),
  startAuditExport: (projectId, payload) => api.post(`/projects/${projectId}/audit-export`, payload || {}),
  getAuditExportStatus: (exportId) => api.get(`/audit-export/${exportId}/status`),
  deleteProject: (projectId) => api.delete(`/projects/${projectId}`),

  // Builder companies (v2.6.2)
  listBuilderCompanies: (userId) => api.get('/builder-companies', { params: { user_id: userId } }),
  createBuilderCompany: (data) => api.post('/builder-companies', data),
  deleteBuilderCompany: (companyId, userId) => api.delete(`/builder-companies/${companyId}`, { params: { user_id: userId } }),

  exportProject: (projectId) => api.get(`/projects/${projectId}/export`, { responseType: 'blob' }),
  export1099Data: (projectId, year) => api.get(`/projects/${projectId}/export-1099-data`, { params: { year }, responseType: 'blob' }),
  getContractorReport: (projectId, year) => api.get(`/projects/${projectId}/contractor-report`, { params: { year } }),
  getProjectLimits: (userId) => api.get(`/users/${userId}/project-limits`),

  // Custom Lien Waiver Template
  uploadProjectLienTemplate: (projectId, formData) => api.post(`/projects/${projectId}/lien-waiver-template`, formData),
  deleteProjectLienTemplate: (projectId) => api.delete(`/projects/${projectId}/lien-waiver-template`),
  getProjectLienTemplate: (projectId) => api.get(`/projects/${projectId}/lien-waiver-template`, { responseType: 'blob' }),
  uploadProjectDrawForm: (projectId, formData) => api.post(`/projects/${projectId}/draw-request-form`, formData),
  deleteProjectDrawForm: (projectId) => api.delete(`/projects/${projectId}/draw-request-form`),
  getProjectDrawForm: (projectId) => api.get(`/projects/${projectId}/draw-request-form`, { responseType: 'blob' }),
  renderProjectDrawForm: (projectId) => api.get(`/projects/${projectId}/draw-request-form/render`),
  getProjectDrawFormMap: (projectId) => api.get(`/projects/${projectId}/draw-request-form/map`),
  saveProjectDrawFormMap: (projectId, fieldMap) => api.put(`/projects/${projectId}/draw-request-form/map`, fieldMap),
  previewFillDrawForm: (projectId, fieldMap) => api.post(`/projects/${projectId}/draw-request-form/preview-fill`, { field_map: fieldMap }, { responseType: 'blob' }),

  // 8-Phase Construction Progress System
  getConstructionPhases: (projectId) => api.get(`/projects/${projectId}/construction-phases`),
  linkPhaseToConstruction: (projectId, phaseNumber, phaseId) => api.post(`/projects/${projectId}/construction-phases/${phaseNumber}/link`, { phase_id: phaseId }),
  unlinkPhaseFromConstruction: (projectId, phaseNumber, phaseId) => api.post(`/projects/${projectId}/construction-phases/${phaseNumber}/unlink`, { phase_id: phaseId }),
  updateConstructionPhase: (projectId, phaseNumber, data) => api.patch(`/projects/${projectId}/construction-phases/${phaseNumber}`, data),
  autoAssignConstructionPhases: (projectId) => api.post(`/projects/${projectId}/construction-phases/auto-assign`),
  reconcilePhasePayments: (projectId, userId) =>
    api.post(`/projects/${projectId}/reconcile-phase-payments`, { user_id: userId }),
  quickMarkPhasePaid: (phaseId, userId) =>
    api.post(`/phases/${phaseId}/quick-mark-paid`, { user_id: userId }),
  quickUnmarkPhasePaid: (phaseId, userId) =>
    api.post(`/phases/${phaseId}/unmark-paid`, { user_id: userId }),
  
  // Construction Phase Inspections
  addInspection: (projectId, phaseNumber, data) => api.post(`/projects/${projectId}/construction-phases/${phaseNumber}/inspections`, data),
  updateInspection: (projectId, phaseNumber, inspectionId, data) => api.patch(`/projects/${projectId}/construction-phases/${phaseNumber}/inspections/${inspectionId}`, data),
  uploadInspectionFile: (projectId, phaseNumber, inspectionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/construction-phases/${phaseNumber}/inspections/${inspectionId}/upload`, formData);
  },
  // v2.5.7: per-draw lien waiver history (preserves compliance trail across multiple draws on the same phase)
  listPhaseLienWaiverHistory: (phaseId) => api.get(`/phases/${phaseId}/lien-waiver-history`),

  // Interest payments log (v2.6.3)
  listInterestPayments: (projectId) => api.get(`/projects/${projectId}/interest-payments`),
  addInterestPayment: (projectId, data) => api.post(`/projects/${projectId}/interest-payments`, data),
  deleteInterestPayment: (paymentId) => api.delete(`/interest-payments/${paymentId}`),


  deleteInspection: (projectId, phaseNumber, inspectionId) => api.delete(`/projects/${projectId}/construction-phases/${phaseNumber}/inspections/${inspectionId}`),
  getInspectionTypes: () => api.get('/inspection-types'),

  // v2.5.6: Line-item (Phase) Inspection Gate
  listPhaseInspections: (phaseId) => api.get(`/phases/${phaseId}/inspections`),
  addPhaseInspection: (phaseId, data) => api.post(`/phases/${phaseId}/inspections`, data),
  uploadPhaseInspectionFile: (phaseId, inspectionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/phases/${phaseId}/inspections/${inspectionId}/upload`, formData);
  },
  overridePhaseInspection: (phaseId, inspectionId, overrideNote) =>
    api.patch(`/phases/${phaseId}/inspections/${inspectionId}/override`, { override_note: overrideNote }),
  deletePhaseInspection: (phaseId, inspectionId) => api.delete(`/phases/${phaseId}/inspections/${inspectionId}`),

  // Phases
  createPhase: (data) => api.post('/phases', data),
  getPhases: (projectId) => api.get('/phases', { params: { project_id: projectId } }),

  // Multi-unit / mixed-use — unit types + per-unit cost breakdown
  getProjectUnits: (projectId) => api.get(`/projects/${projectId}/units`),
  addProjectUnit: (projectId, data) => api.post(`/projects/${projectId}/units`, data),
  updateProjectUnit: (projectId, unitId, data) => api.patch(`/projects/${projectId}/units/${unitId}`, data),
  deleteProjectUnit: (projectId, unitId) => api.delete(`/projects/${projectId}/units/${unitId}`),
  getProjectUnitCosts: (projectId) => api.get(`/projects/${projectId}/unit-costs`),
  updatePhase: (phaseId, data) => api.patch(`/phases/${phaseId}`, data),
  deletePhase: (phaseId) => api.delete(`/phases/${phaseId}`),
  bulkCreatePrePaidExpenses: (projectId, items, constructionPhaseNumber = 1) =>
    api.post(`/projects/${projectId}/prepaid-expenses/bulk`, { items, construction_phase_number: constructionPhaseNumber }),

  // Quick Capture - global receipt/invoice capture with AI extraction + duplicate detection
  quickCaptureExtract: (userId, file, projectId = null) => {
    const formData = new FormData();
    formData.append('user_id', userId);
    if (projectId) formData.append('project_id', projectId);
    formData.append('file', file);
    return api.post('/quick-capture/extract', formData);
  },
  quickCaptureFile: (data) => api.post('/quick-capture/file', data),
  quickCaptureDiscard: (fileId) => api.delete(`/quick-capture/discard/${fileId}`),
  
  uploadPaymentProof: (phaseId, paymentType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('payment_type', paymentType);
    return api.post(`/phases/${phaseId}/upload-payment-proof`, formData);
  },
  getPaymentProof: (phaseId, paymentType) => api.get(`/phases/${phaseId}/payment-proof/${paymentType}`, { responseType: 'blob' }),

  // Milestones
  getMilestoneTemplates: () => api.get('/milestone-templates'),
  addMilestone: (phaseId, name, description) => api.post(`/phases/${phaseId}/milestones`, null, { params: { milestone_name: name, milestone_description: description } }),
  addMilestonesFromTemplate: (phaseId, trade) => api.post(`/phases/${phaseId}/milestones/from-template`, null, { params: { trade } }),
  updateMilestone: (phaseId, milestoneId, data) => api.patch(`/phases/${phaseId}/milestones/${milestoneId}`, data),
  deleteMilestone: (phaseId, milestoneId) => api.delete(`/phases/${phaseId}/milestones/${milestoneId}`),

  // Phase Receipts (Multiple per phase)
  // Accepts EITHER a ready-built FormData, or a raw File + options
  // ({ amount, addToActualCost }). Unified so all callers work; a duplicate
  // File-only definition previously shadowed this and broke FormData callers
  // (threw a TypeError -> "Failed to upload receipt" / "Failed to save payment").
  uploadPhaseReceipt: (phaseId, fileOrFormData, opts = {}) => {
    let fd;
    if (fileOrFormData instanceof FormData) {
      fd = fileOrFormData;
    } else {
      fd = new FormData();
      fd.append('file', fileOrFormData, fileOrFormData?.name || 'receipt.jpg');
      if (opts.amount != null) fd.append('amount', String(opts.amount));
      fd.append('add_to_actual_cost', String(!!opts.addToActualCost));
    }
    return api.post(`/phases/${phaseId}/receipts`, fd);
  },
  downloadPhaseReceipt: (phaseId, receiptId) => api.get(`/phases/${phaseId}/receipts/${receiptId}/download`, { responseType: 'blob' }),
  deletePhaseReceipt: (phaseId, receiptId) => api.delete(`/phases/${phaseId}/receipts/${receiptId}`),
  updatePhaseReceipt: (phaseId, receiptId, data) => api.patch(`/phases/${phaseId}/receipts/${receiptId}`, null, { params: data }),
  updatePhaseNotes: (phaseId, notes) => api.patch(`/phases/${phaseId}/notes`, { notes }),

  // Phase Payments with Receipt Support
  addPhasePayment: (phaseId, data) => api.post(`/phases/${phaseId}/payments`, data),
  markPhaseAsPaidBypass: (phaseId, data) => api.post(`/phases/${phaseId}/mark-paid-bypass`, data),
  
  // Phase Compliance (Lien Waiver, Inspection, Completion Photos)
  getPhaseComplianceStatus: (phaseId) => api.get(`/phases/${phaseId}/compliance-status`),
  getPhaseComplianceDocuments: (phaseId) => api.get(`/phases/${phaseId}/compliance-documents`),
  updatePhaseComplianceSettings: (phaseId, settings) => api.patch(`/phases/${phaseId}/compliance-settings`, settings),
  requestLienWaiver: (phaseId) => api.post(`/phases/${phaseId}/request-lien-waiver`),
  requestComplianceDocs: (phaseId, invoiceAmount, paymentInfo = {}) => api.post(`/phases/${phaseId}/request-compliance-docs`, { 
    invoice_amount: invoiceAmount,
    is_partial: paymentInfo.is_partial,
    total_phase_cost: paymentInfo.total_phase_cost,
    already_paid: paymentInfo.already_paid,
    remaining_after_payment: paymentInfo.remaining_after_payment,
    include_w9: paymentInfo.include_w9,
    include_coi: paymentInfo.include_coi,
    include_inspection: paymentInfo.include_inspection,
    // v2.5.8: per-doc opt-out
    include_lien_waiver: paymentInfo.include_lien_waiver,
    include_photos: paymentInfo.include_photos,
  }),
  requestNewLienWaiver: (phaseId, amount) => api.post(`/phases/${phaseId}/request-new-lien-waiver`, { amount }),
  skipPhaseCompliance: (phaseId, payload = {}) => api.post(`/phases/${phaseId}/skip-compliance`, payload),
  
  // Contractor Compliance Portal
  getContractorPortalData: (token, phaseId) => api.get(`/contractor-portal/${token}`, { params: { phase_id: phaseId } }),
  uploadPortalLienWaiver: (token, formData) => api.post(`/contractor-portal/${token}/lien-waiver`, formData),
  uploadCompletionPhotos: (token, formData) => api.post(`/contractor-portal/${token}/completion-photos`, formData),
  uploadInspectionReport: (token, formData) => api.post(`/contractor-portal/${token}/inspection-report`, formData),
  submitComplianceESignature: (token, phaseId, signatureData, formFields) => api.post(`/contractor-portal/${token}/esign-lien-waiver`, {
    signature_data: signatureData,
    phase_id: phaseId,
    ...formFields
  }),
  downloadComplianceLienWaiverPdf: (token, phaseId) => api.get(`/contractor-portal/${token}/lien-waiver-pdf`, {
    params: { phase_id: phaseId },
    responseType: 'blob'
  }),
  
  // AI Receipt Extraction
  extractReceiptData: (formData) => api.post('/extract-receipt', formData),
  
  // AI W-9 Extraction
  extractW9Data: (formData) => api.post('/extract-w9', formData, {
        timeout: 60000, // 60 second timeout for AI extraction
  }),
  
  // AI Bid Extraction
  extractBidData: (formData) => api.post('/extract-bid', formData, {
        timeout: 150000, // 150s — scanned PDFs use the slower vision path
  }),
  
  // Multi-Trade Bid Management
  createMultiTradeBids: (userId, data) => api.post(`/bids/create-multi-trade?user_id=${userId}`, data),
  getBidsByTrade: (userId, projectId) => api.get(`/bids/compare-by-trade/${userId}`, { params: { project_id: projectId } }),
  autoConsolidateUploadedBids: (userId) => api.post('/bids/auto-consolidate-uploaded', null, { params: { user_id: userId } }),
  // Error telemetry (owner/admin)
  getErrorLogs: (includeResolved = false) => api.get('/telemetry/errors', { params: { include_resolved: includeResolved } }),
  resolveErrorLog: (id) => api.post(`/telemetry/errors/${id}/resolve`),
  deleteTradeBid: (bidId, userId) => api.delete(`/bids/trade/${bidId}?user_id=${userId}`),
  
  // AI Material Takeoffs
  analyzeForTakeoff: (formData) => api.post('/ai-takeoff', formData, {
        timeout: 120000, // 120 second timeout - allows for multi-page analysis
  }),
  
  // PDF Page Preview for AI Takeoff
  getPdfPreview: (formData) => api.post('/pdf-preview', formData, {
        timeout: 30000,
  }),

  // Documents
  uploadDocument: (formData) => api.post('/documents/upload', formData),
  getDocuments: (projectId, phaseId) => api.get('/documents', { params: { project_id: projectId, phase_id: phaseId } }),
  downloadDocument: (documentId) => api.get(`/documents/${documentId}/download`, { responseType: 'blob' }),
  deleteDocument: (documentId) => api.delete(`/documents/${documentId}`),

  // Project Photos
  uploadProjectPhotos: (projectId, formData) => api.post(`/projects/${projectId}/photos`, formData),
  getProjectPhotos: (projectId) => api.get(`/projects/${projectId}/photos`),
  deleteProjectPhoto: (projectId, photoId) => api.delete(`/projects/${projectId}/photos/${photoId}`),

  // Project Cover Image (for dashboard cards)
  uploadProjectCoverImage: (projectId, formData) => api.post(`/projects/${projectId}/cover-image`, formData),
  deleteProjectCoverImage: (projectId) => api.delete(`/projects/${projectId}/cover-image`),

  // Spec Sheets
  uploadSpecSheet: (projectId, formData) => api.post(`/projects/${projectId}/spec-sheets`, formData),
  getSpecSheets: (projectId) => api.get(`/projects/${projectId}/spec-sheets`),
  deleteSpecSheet: (projectId, specSheetId) => api.delete(`/projects/${projectId}/spec-sheets/${specSheetId}`),

  // Rate History (for adjustable rate loans)
  getRateHistory: (projectId) => api.get(`/projects/${projectId}/rate-history`),
  addRateHistory: (projectId, data) => api.post(`/projects/${projectId}/rate-history`, data),
  updateRateHistory: (projectId, entryId, data) => api.patch(`/projects/${projectId}/rate-history/${entryId}`, data),
  deleteRateHistory: (projectId, entryId) => api.delete(`/projects/${projectId}/rate-history/${entryId}`),
  getInterestCalculation: (projectId) => api.get(`/projects/${projectId}/interest-calculation`),
  getInterestSchedule: (projectId) => api.get(`/projects/${projectId}/interest-schedule`),
  getDrawProjection: (projectId) => api.get(`/projects/${projectId}/draw-projection`),
  computeDrawProjection: (projectId, body) => api.post(`/projects/${projectId}/draw-projection`, body || {}),

  // Monthly Loan Statements (interest + draw/activity; per-project + portfolio)
  getProjectStatement: (projectId, month) =>
    api.get(`/projects/${projectId}/statement`, { params: month ? { month } : {} }),
  getPortfolioStatement: (userId, month) =>
    api.get(`/portfolio/statement`, { params: { user_id: userId, ...(month ? { month } : {}) } }),
  projectStatementPdfUrl: (projectId, month) =>
    `${API_URL}/api/projects/${projectId}/statement/pdf${month ? `?month=${month}` : ''}`,
  projectInterestSummaryPdfUrl: (projectId, month) =>
    `${API_URL}/api/projects/${projectId}/interest-summary/pdf${month ? `?month=${month}` : ''}`,
  portfolioStatementPdfUrl: (userId, month) =>
    `${API_URL}/api/portfolio/statement/pdf?user_id=${userId}${month ? `&month=${month}` : ''}`,

  // DrawReady Assistant (Claude + Whisper)
  assistantChat: (userId, message, history) =>
    api.post('/assistant/chat', { user_id: userId, message, history: history || [] }),
  assistantExecute: (userId, action) =>
    api.post('/assistant/execute', { user_id: userId, action }),
  assistantTranscribe: (blob) => {
    const fd = new FormData();
    fd.append('file', blob, 'voice.webm');
    return api.post('/assistant/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  assistantReceiptCheck: (phaseId, userId, file) => {
    const fd = new FormData();
    fd.append('phase_id', phaseId);
    fd.append('user_id', userId);
    fd.append('file', file, file.name || 'receipt.jpg');
    return api.post('/assistant/receipt-check', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  assistantReceiptCommit: (payload) => api.post('/assistant/receipt-commit', payload),
  assistantReceiptDiscard: (fileId) => api.delete(`/quick-capture/discard/${fileId}`),

  // Draw History (for tracking draw dates and balances)
  getDrawHistory: (projectId) => api.get(`/projects/${projectId}/draw-history`),
  getSubmittedDraws: (projectId, userId) => api.get(`/projects/${projectId}/submitted-draws`, { params: { user_id: userId } }),
  addDrawHistory: (projectId, data) => api.post(`/projects/${projectId}/draw-history`, data),
  updateDrawHistory: (projectId, drawId, data) => api.put(`/projects/${projectId}/draw-history/${drawId}`, data),
  deleteDrawHistory: (projectId, drawId) => api.delete(`/projects/${projectId}/draw-history/${drawId}`),

  // Contractors
  createContractor: (userIdOrData, data) => {
    // Support both (userId, data) and (data) signatures for backward compatibility
    if (data === undefined) {
      // Old style: createContractor(data) where user_id is in data
      return api.post('/contractors', userIdOrData, { params: { user_id: userIdOrData.user_id } });
    } else {
      // New style: createContractor(userId, data)
      return api.post('/contractors', data, { params: { user_id: userIdOrData } });
    }
  },
  getContractors: (projectId) => api.get('/contractors', { params: { project_id: projectId } }),
  getContractorsByUser: (userId) => api.get('/contractors', { params: { user_id: userId } }),
  updateContractor: (contractorId, data) => api.patch(`/contractors/${contractorId}`, data),
  deleteContractor: (contractorId) => api.delete(`/contractors/${contractorId}`),
  uploadW9: (contractorId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Don't set Content-Type header - axios will set it automatically with the correct boundary for FormData
    return api.post(`/contractors/${contractorId}/w9-upload`, formData);
  },
  downloadW9: (contractorId, fileId) => api.get(`/contractors/${contractorId}/w9-download/${fileId}`, { responseType: 'blob' }),
  // Don't set Content-Type header for FormData - axios sets it automatically with boundary
  uploadContractorW9: (contractorId, formData) => api.post(`/contractors/${contractorId}/w9-upload`, formData),
  uploadContractorCOI: (contractorId, formData) => api.post(`/contractors/${contractorId}/coi-upload`, formData),
  downloadCOI: (contractorId, fileId) => api.get(`/contractors/${contractorId}/coi-download/${fileId}`, { responseType: 'blob' }),
  
  // Phase compliance document uploads (owner uploading on behalf of contractor)
  // Don't set Content-Type header for FormData - axios sets it automatically with boundary
  uploadPhaseLienWaiver: (phaseId, formData) => api.post(`/phases/${phaseId}/lien-waiver-upload`, formData),
  uploadPhaseCompletionPhotos: (phaseId, formData) => api.post(`/phases/${phaseId}/completion-photos-upload`, formData),
  
  // Custom Trades
  getTrades: (userId) => api.get('/trades', { params: { user_id: userId } }),
  createTrade: (userId, name) => api.post('/trades', null, { params: { user_id: userId, name } }),
  deleteTrade: (tradeName, userId) => api.delete(`/trades/${tradeName}`, { params: { user_id: userId } }),
  
  // Scope of Work Templates
  getScopeTemplates: (userId, tradeType = null) => api.get('/scope-templates', { 
    params: { user_id: userId, trade_type: tradeType } 
  }),
  getScopeTemplate: (templateId, userId) => api.get(`/scope-templates/${templateId}`, { 
    params: { user_id: userId } 
  }),
  createScopeTemplate: (userId, data) => api.post('/scope-templates', data, { 
    params: { user_id: userId } 
  }),
  updateScopeTemplate: (templateId, userId, data) => api.put(`/scope-templates/${templateId}`, data, { 
    params: { user_id: userId } 
  }),
  deleteScopeTemplate: (templateId, userId) => api.delete(`/scope-templates/${templateId}`, { 
    params: { user_id: userId } 
  }),
  
  // Contractor Payments
  createContractorPayment: (data) => api.post('/contractor-payments', data),
  getContractorPayments: (userId, contractorId) => api.get('/contractor-payments', { 
    params: { user_id: userId, contractor_id: contractorId } 
  }),
  getContractorPaymentsByUser: (userId) => api.get('/contractor-payments', { params: { user_id: userId } }),
  deleteContractorPayment: (paymentId) => api.delete(`/contractor-payments/${paymentId}`),
  uploadLienWaiver: (paymentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/contractor-payments/${paymentId}/lien-waiver-upload`, formData);
  },
  get1099Report: (userId, year) => api.get('/1099-report', { params: { user_id: userId, year } }),
  get1099ReportForContractor: (contractorId, year, userId) => api.get(`/contractors/${contractorId}/1099-report`, { params: { year, user_id: userId } }),

  // Stripe Contractor Payments
  createContractorPaymentCheckout: (data) => api.post('/contractor-payments/stripe/checkout', data),
  getContractorPaymentStatus: (sessionId) => api.get(`/contractor-payments/stripe/status/${sessionId}`),

  // ACH Direct Bank Payments
  setupContractorBankAccount: (contractorId) => api.post(`/contractors/${contractorId}/setup-bank-account`, { 
    origin_url: window.location.origin 
  }),
  getContractorBankStatus: (contractorId) => api.get(`/contractors/${contractorId}/bank-status`),
  sendACHPayment: (data, userId) => api.post('/ach-payments/send', data, { params: { user_id: userId } }),
  getACHPaymentStatus: (paymentId) => api.get(`/ach-payments/${paymentId}/status`),
  getACHPaymentHistory: (userId, limit = 50) => api.get('/ach-payments/history', { params: { user_id: userId, limit } }),

  // Plaid - User Bank Account Connection
  createPlaidLinkToken: (userId) => api.post('/plaid/create-link-token', {}, { params: { user_id: userId } }),
  exchangePlaidToken: (data) => api.post('/plaid/exchange-token', data),
  getUserBankAccount: (userId) => api.get('/plaid/bank-account', { params: { user_id: userId } }),
  disconnectBankAccount: (userId) => api.delete('/plaid/bank-account', { params: { user_id: userId } }),

  // Mortgage Transaction Import
  getMortgageTransactions: (userId, year, month) => api.get('/mortgage-import/transactions', { params: { user_id: userId, year, month } }),
  assignMortgageToProperty: (data) => api.post('/mortgage-import/assign', data),
  updatePropertyLoanDetails: (propertyId, data) => api.patch(`/rental-properties/${propertyId}/loan-details`, data),

  // Rental Properties
  getRentalProperties: (userId) => api.get('/rental-properties', { params: { user_id: userId } }),
  createRentalProperty: (data) => api.post('/rental-properties', data),
  updateRentalProperty: (propertyId, data) => api.put(`/rental-properties/${propertyId}`, data),
  deleteRentalProperty: (propertyId) => api.delete(`/rental-properties/${propertyId}`),

  // Rental Income
  getRentalIncome: (userId, propertyId, year, quarter) => api.get('/rental-income', { 
    params: { user_id: userId, property_id: propertyId, year, quarter } 
  }),
  createRentalIncome: (data) => api.post('/rental-income', data),
  deleteRentalIncome: (incomeId) => api.delete(`/rental-income/${incomeId}`),

  // Rental Expenses
  getRentalExpenses: (userId, propertyId, year, quarter) => api.get('/rental-expenses', { 
    params: { user_id: userId, property_id: propertyId, year, quarter } 
  }),
  createRentalExpense: (data) => api.post('/rental-expenses', data),
  updateRentalExpense: (expenseId, data) => api.patch(`/rental-expenses/${expenseId}`, data),
  deleteRentalExpense: (expenseId) => api.delete(`/rental-expenses/${expenseId}`),
  uploadExpenseReceipt: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/rental-expenses/upload-receipt', formData);
  },
  getRentalExpenseReceipt: (expenseId) => api.get(`/rental-expenses/${expenseId}/receipt`, { responseType: 'blob' }),

  // Monthly Property Costs
  getMonthlyCosts: (userId, propertyId, year) => api.get('/monthly-costs', { 
    params: { user_id: userId, property_id: propertyId, year } 
  }),
  createMonthlyCost: (userId, data) => api.post('/monthly-costs', data, { params: { user_id: userId } }),
  updateMonthlyCost: (costId, data) => api.patch(`/monthly-costs/${costId}`, data),
  deleteMonthlyCost: (costId) => api.delete(`/monthly-costs/${costId}`),
  getMonthlyCostSummary: (propertyId, year) => api.get(`/monthly-costs/summary/${propertyId}`, { params: { year } }),

  // Financial Reports
  getForm8825: (userId, year) => api.get('/reports/form-8825', { params: { user_id: userId, year } }),
  getProfitLoss: (userId, year, quarter, propertyId) => api.get('/reports/profit-loss', { 
    params: { user_id: userId, year, quarter, property_id: propertyId } 
  }),
  getBalanceSheet: (userId) => api.get('/reports/balance-sheet', { params: { user_id: userId } }),
  getQuarterlyReport: (userId, year, quarter) => api.get('/reports/quarterly', { 
    params: { user_id: userId, year, quarter } 
  }),
  getExpenseCategories: () => api.get('/reports/categories'),
  getOverallReport: (userId, year) => api.get('/reports/overall', { params: { user_id: userId, year } }),
  // Branded PDF report downloads (blob)
  getProfitLossPdf: (userId, year, quarter, propertyId) => api.get('/reports/profit-loss/pdf', {
    params: { user_id: userId, year, quarter, property_id: propertyId }, responseType: 'blob'
  }),
  getBalanceSheetPdf: (userId) => api.get('/reports/balance-sheet/pdf', { params: { user_id: userId }, responseType: 'blob' }),
  getQuarterlyPdf: (userId, year, quarter) => api.get('/reports/quarterly/pdf', {
    params: { user_id: userId, year, quarter }, responseType: 'blob'
  }),
  getForm8825Pdf: (userId, year) => api.get('/reports/form-8825/pdf', { params: { user_id: userId, year }, responseType: 'blob' }),

  // Task Statistics
  getTaskStats: (userId) => api.get('/tasks/stats', { params: { user_id: userId } }),

  // General Business Expenses
  getGeneralExpenses: (userId, year, quarter) => api.get('/general-expenses', { 
    params: { user_id: userId, year, quarter } 
  }),
  createGeneralExpense: (data) => api.post('/general-expenses', data),
  deleteGeneralExpense: (expenseId) => api.delete(`/general-expenses/${expenseId}`),
  updateGeneralExpense: (expenseId, data) => api.put(`/general-expenses/${expenseId}`, data),

  // PayForms
  createPayForm: (data) => api.post('/payforms', data),
  sendPayForm: (payformId) => api.post(`/payforms/${payformId}/send`),
  getPayForms: (projectId) => api.get('/payforms', { params: { project_id: projectId } }),

  // Form Requests (W-9, Lien Waiver, COI)
  createFormRequest: (data, hostUrl) => api.post('/form-requests', data, { params: { host_url: hostUrl } }),
  generateLienWaiver: (data) => api.post('/generate-lien-waiver', data, { responseType: 'blob' }),
  getFormRequests: (projectId, contractorId) => api.get('/form-requests', { 
    params: { project_id: projectId, contractor_id: contractorId } 
  }),
  getFormRequestByToken: (uploadToken) => api.get(`/form-requests/token/${uploadToken}`),
  getFormRequestDocuments: (uploadToken) => api.get(`/form-requests/${uploadToken}/documents`),
  uploadFormDocument: (uploadToken, formType, file, expirationDate) => {
    const formData = new FormData();
    formData.append('form_type', formType);
    formData.append('file', file);
    if (expirationDate) formData.append('expiration_date', expirationDate);
    return api.post(`/form-requests/upload/${uploadToken}`, formData);
  },
  // v2.6: Per-project COI list (owner-side compliance view)
  getProjectCOIs: (projectId) => api.get(`/projects/${projectId}/cois`),
  submitESignature: (uploadToken, formType, signatureData, formFields = {}) => api.post(`/form-requests/${uploadToken}/esign`, {
    signature_data: signatureData,
    form_type: formType,
    ...formFields
  }),
  downloadLienWaiverPdf: (uploadToken) => api.get(`/form-requests/${uploadToken}/download-lien-waiver`, { responseType: 'blob' }),
  deleteFormRequest: (requestId) => api.delete(`/form-requests/${requestId}`),
  // Send W-9 request to contractor (creates a form request for W-9)
  sendW9Request: (contractorId, userId) => api.post('/form-requests', {
    contractor_id: contractorId,
    form_types: ['w9'],
    project_id: null  // Global W-9 request not tied to a specific project
  }, { params: { host_url: window.location.origin, user_id: userId } }),
  // Send Insurance/COI request to contractor
  sendInsuranceRequest: (contractorId, userId) => api.post('/form-requests', {
    contractor_id: contractorId,
    form_types: ['coi'],
    project_id: null  // Global COI request not tied to a specific project
  }, { params: { host_url: window.location.origin, user_id: userId } }),
  
  // Lien Waiver Status by Phase
  getLienWaiverStatus: (projectId) => api.get(`/projects/${projectId}/lien-waiver-status`),

  // Excel Import
  previewExcelImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/projects/import/preview', formData);
  },
  importProjectFromExcel: (file, userId, projectName, columnMapping) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId);
    formData.append('project_name', projectName);
    formData.append('column_mapping', JSON.stringify(columnMapping));
    return api.post('/projects/import', formData);
  },
  importProjectWithPhases: (userId, importData) => {
    return api.post('/projects/import-with-phases', {
      user_id: userId,
      ...importData
    });
  },

  // Paint Colors
  createPaintColor: (data) => api.post('/paint-colors', data),
  getPaintColors: (projectId) => api.get('/paint-colors', { params: { project_id: projectId } }),
  updatePaintColor: (id, data) => api.put(`/paint-colors/${id}`, data),
  deletePaintColor: (id) => api.delete(`/paint-colors/${id}`),

  // AI Predictions
  predictCosts: (projectId) => api.post('/ai/predict', null, { params: { project_id: projectId } }),

  // Subscription & Billing
  getFeatureFlags: (userId) => api.get(`/feature-flags/${userId}`),
  getFeatureGates: (userId) => api.get('/feature-gates', { params: { user_id: userId } }),
  bidImportAnalyze: (projectId, userId, file) => {
    const fd = new FormData();
    fd.append('file', file, file.name || 'bid-sheet.xlsx');
    fd.append('user_id', userId);
    return api.post(`/projects/${projectId}/bid-import/analyze`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
  },
  bidImportApply: (projectId, payload) => api.post(`/projects/${projectId}/bid-import/apply`, payload, { timeout: 120000 }),
  projectBenchmark: (projectId, userId) => api.get(`/projects/${projectId}/benchmark`, { params: { user_id: userId } }),
  getFeatureGatesStatus: (userId) => api.get('/admin/feature-gates/status', { headers: { 'X-User-Id': userId } }),
  rolloutFeatureGate: (userId, key, isPublic) =>
    api.post(`/admin/feature-gates/${key}/rollout`, null, { params: { public: isPublic }, headers: { 'X-User-Id': userId } }),
  validatePromoCode: (code) => api.post('/promo-codes/validate', null, { params: { code } }),

  // Admin
  adminGetUsers: (adminUserId) => api.get('/admin/users', { params: { admin_user_id: adminUserId } }),
  adminUpdateUser: (userId, adminUserId, data) => api.patch(`/admin/users/${userId}`, null, { params: { admin_user_id: adminUserId, ...data } }),
  adminRevokeAccess: (userId, adminUserId) => api.post(`/admin/users/${userId}/revoke-access`, null, { params: { admin_user_id: adminUserId } }),
  adminDeleteUser: (userId, adminUserId) => api.delete(`/admin/users/${userId}`, { params: { admin_user_id: adminUserId } }),
  adminGetStats: (adminUserId) => api.get('/admin/stats', { params: { admin_user_id: adminUserId } }),
  adminGetPromoCodes: (adminUserId) => api.get('/admin/promo-codes', { params: { admin_user_id: adminUserId } }),
  adminCreatePromoCode: (adminUserId, data) => api.post('/admin/promo-codes', null, { params: { admin_user_id: adminUserId, ...data } }),
  adminDeletePromoCode: (codeId, adminUserId) => api.delete(`/admin/promo-codes/${codeId}`, { params: { admin_user_id: adminUserId } }),

  // Affiliates
  getAffiliates: () => api.get('/admin/affiliates'),
  createAffiliate: (data) => api.post('/admin/affiliates', data),
  updateAffiliate: (affiliateId, data) => api.patch(`/admin/affiliates/${affiliateId}`, data),
  deleteAffiliate: (affiliateId) => api.delete(`/admin/affiliates/${affiliateId}`),
  trackAffiliateClick: (code) => api.get(`/affiliate/track/${code}`),
  recordAffiliateSignup: (data) => api.post('/affiliate/record-signup', data),

  // Discount Codes
  getDiscountCodes: () => api.get('/admin/discount-codes'),
  createDiscountCode: (data) => api.post('/admin/discount-codes', data),
  updateDiscountCode: (codeId, data) => api.patch(`/admin/discount-codes/${codeId}`, data),
  deleteDiscountCode: (codeId) => api.delete(`/admin/discount-codes/${codeId}`),
  validateDiscountCode: (code, tier) => api.post('/discount-codes/validate', { code, tier }),
  redeemDiscountCode: (code, userId) => api.post('/discount-codes/redeem', { code, user_id: userId }),

  // Admin Bid Fix
  adminFixBidResponse: (bidRequestId, data) => api.patch(`/admin/fix-bid-response/${bidRequestId}`, data),
  adminLookupBidRequests: (params) => api.get('/admin/lookup-bid-requests', { params }),
  
  // Admin Subscription Management
  adminGetDuplicateSubscriptions: () => api.get('/admin/duplicate-subscriptions'),
  adminCancelSubscription: (subscriptionId, immediate = false) => 
    api.post(`/admin/cancel-subscription/${subscriptionId}`, null, { params: { immediate } }),

  // Signature Management
  uploadSignature: (userId, signatureImage) => api.post(`/auth/signature/${userId}`, { signature_image: signatureImage }),
  getSignature: (userId) => api.get(`/auth/signature/${userId}`),
  deleteSignature: (userId) => api.delete(`/auth/signature/${userId}`),

  // Dashboard Preference
  getDashboardPreference: (userId) => api.get(`/auth/dashboard-preference/${userId}`),
  updateDashboardPreference: (userId, preference, markFeatureSeen = false) => api.put(`/auth/dashboard-preference/${userId}`, { dashboard_preference: preference, mark_feature_seen: markFeatureSeen }),

  // Reimbursement Requests
  getReimbursementHistory: (projectId) => api.get('/reimbursement/history', { params: { project_id: projectId } }),
  downloadDrawRequestPdf: (requestId) => api.get(`/reimbursement/${requestId}/draw-request-pdf`, { responseType: 'blob' }),
  downloadDrawSummaryPdf: (requestId) => api.get(`/reimbursement/${requestId}/summary-pdf`, { responseType: 'blob' }),
  downloadDrawBankFormPdf: (requestId, style = 'hills') => api.get(`/reimbursement/${requestId}/bank-form-pdf`, { params: { style }, responseType: 'blob' }),
  downloadDrawPackage: (requestId, style = 'hills') => api.get(`/reimbursement/${requestId}/package`, { params: { style }, responseType: 'blob' }),
  generateReimbursementRequest: (data) => api.post('/reimbursement/generate', data, { responseType: 'blob' }),
  submitDrawToBank: (data) => api.post('/reimbursement/generate', data),
  confirmReimbursementDeposit: (requestId, depositInfo) => api.post(`/reimbursement/${requestId}/confirm-deposit`, depositInfo),
  deleteReimbursementRequest: (requestId) => api.delete(`/reimbursement/${requestId}`),
  updateReimbursementRequest: (requestId, data) => api.patch(`/reimbursement/${requestId}`, data),

  // Draw confirmation reminders (v2.9.11)
  getPendingDrawConfirmations: (userId) => api.get('/draws/pending-confirmations', { params: { user_id: userId } }),
  snoozeDrawConfirmation: (requestId) => api.post(`/draws/${requestId}/snooze`),
  discardDrawConfirmation: (requestId) => api.post(`/draws/${requestId}/discard`),
  getDrawByToken: (token) => api.get(`/draws/confirm-link/${token}`),
  confirmDrawByToken: (token, depositInfo) => api.post(`/draws/confirm-link/${token}/confirm`, depositInfo),
  snoozeDrawByToken: (token) => api.post(`/draws/confirm-link/${token}/snooze`),
  discardDrawByToken: (token) => api.post(`/draws/confirm-link/${token}/discard`),

  // Phase Payments
  createPhasePayment: (data) => api.post('/phase-payments', data),
  getPhasePayments: (phaseId) => api.get(`/phases/${phaseId}/payments`),
  getPhasePaymentsByProject: (projectId) => api.get('/phase-payments', { params: { project_id: projectId } }),
  deletePhasePayment: (paymentId) => api.delete(`/phase-payments/${paymentId}`),
  updatePhasePayment: (paymentId, data) => api.patch(`/phase-payments/${paymentId}`, data),
  resetPhasePayments: (phaseId, data = {}) => api.post(`/phases/${phaseId}/reset-payments`, data),

  // Receipt Scanning
  scanReceipt: (phaseId, projectId, file) => {
    const formData = new FormData();
    formData.append('project_id', projectId);
    formData.append('file', file);
    return api.post(`/phases/${phaseId}/receipts/scan`, formData);
  },
  getPhaseReceipts: (phaseId) => api.get(`/phases/${phaseId}/receipts`),
  
  // Sync supplier payments (for migrating old data)
  syncSupplierPayments: () => api.post('/sync-supplier-payments'),
  
  // Expense Receipt Scanning (Soft Costs)
  scanExpenseReceipt: (expenseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/project-expenses/${expenseId}/scan-receipt`, formData);
  },
  getProjectExpenseReceipt: (expenseId) => api.get(`/project-expenses/${expenseId}/receipt`, { responseType: 'blob' }),
  updateReceipt: (receiptId, data) => api.patch(`/receipts/${receiptId}`, null, { params: data }),
  deleteReceipt: (receiptId) => api.delete(`/receipts/${receiptId}`),
  getReceiptImage: (receiptId) => api.get(`/receipts/${receiptId}/image`, { responseType: 'blob' }),
  applyReceiptsToPhase: (phaseId) => api.post(`/phases/${phaseId}/receipts/apply-total`),
  markReceiptsSubmitted: (projectId, receiptIds) => api.post(`/projects/${projectId}/receipts/mark-submitted`, { receipt_ids: receiptIds }),
  deleteSubmittedReceipts: (projectId) => api.delete(`/projects/${projectId}/receipts/submitted`),

  // Project Expenses
  createProjectExpense: (data) => api.post('/project-expenses', data),
  getProjectExpenses: (projectId) => api.get('/project-expenses', { params: { project_id: projectId } }),
  updateProjectExpense: (expenseId, data) => api.patch(`/project-expenses/${expenseId}`, data),
  deleteProjectExpense: (expenseId) => api.delete(`/project-expenses/${expenseId}`),
  addExpensePayment: (expenseId, data) => api.post(`/project-expenses/${expenseId}/add-payment`, data),
  deleteExpensePayment: (expenseId, paymentId) => api.delete(`/project-expenses/${expenseId}/payments/${paymentId}`),

  // Budget Templates
  createTemplate: (data) => api.post('/templates', data),
  getTemplates: (userId) => api.get('/templates', { params: { user_id: userId } }),
  getTemplate: (templateId) => api.get(`/templates/${templateId}`),
  deleteTemplate: (templateId) => api.delete(`/templates/${templateId}`),
  exportTemplate: (templateId) => api.get(`/templates/${templateId}/export`, { responseType: 'blob' }),
  createProjectFromTemplate: (templateId, projectData) => api.post(`/templates/${templateId}/create-project`, projectData),

  // Template Phases
  createTemplatePhase: (data) => api.post('/template-phases', data),
  getTemplatePhases: (templateId) => api.get('/template-phases', { params: { template_id: templateId } }),
  deleteTemplatePhase: (phaseId) => api.delete(`/template-phases/${phaseId}`),

  // Bank Templates
  createBankTemplate: (data) => api.post('/bank-templates', data),
  getBankTemplates: (userId) => api.get('/bank-templates', { params: { user_id: userId } }),
  uploadBankTemplateFile: (bankTemplateId, formData) => api.post(`/bank-templates/${bankTemplateId}/upload`, formData),
  aiMapCostsToBankTemplate: (bankTemplateId, costsText) => api.post(`/bank-templates/${bankTemplateId}/ai-map-costs`, { costs_text: costsText }),
  exportMappedCosts: (bankTemplateId, mappings, projectInfo) => api.post(
    `/bank-templates/${bankTemplateId}/export-mapped-costs`, 
    { mappings, project_info: projectInfo },
    { responseType: 'blob' }
  ),
  convertMappedCostsToPhases: (bankTemplateId, mappings, projectId) => api.post(
    `/bank-templates/${bankTemplateId}/convert-to-phases`,
    { mappings, project_id: projectId }
  ),
  exportProjectBankFormat: (projectId, bankTemplateId) => api.get(`/projects/${projectId}/export-bank-format`, { 
    params: { bank_template_id: bankTemplateId },
    responseType: 'blob'
  }),

  // Lender Library (Phase 1)
  getLenders: (userId) => api.get('/lenders', { params: { user_id: userId } }),
  getLender: (lenderId) => api.get(`/lenders/${lenderId}`),
  createLender: (data) => api.post('/lenders', data),
  updateLender: (lenderId, data) => api.put(`/lenders/${lenderId}`, data),
  deleteLender: (lenderId) => api.delete(`/lenders/${lenderId}`),
  uploadLenderForm: (lenderId, formType, formData) => api.post(`/lenders/${lenderId}/forms/${formType}`, formData),
  downloadLenderForm: (lenderId, formType) => api.get(`/lenders/${lenderId}/forms/${formType}/download`, { responseType: 'blob' }),
  deleteLenderForm: (lenderId, formType) => api.delete(`/lenders/${lenderId}/forms/${formType}`),


  // Financial Analysis
  calculateFinancialAnalysis: (projectId, data) => api.post(`/projects/${projectId}/financial-analysis`, data),
  getFinancialAnalyses: (projectId) => api.get(`/projects/${projectId}/financial-analyses`),

  // Property Categories (formerly Building Components)
  createBuildingComponent: (projectId, data) => api.post(`/projects/${projectId}/building-components`, data),
  getBuildingComponents: (projectId) => api.get(`/projects/${projectId}/building-components`),
  updateBuildingComponent: (componentId, data) => api.patch(`/building-components/${componentId}`, data),
  deleteBuildingComponent: (componentId) => api.delete(`/building-components/${componentId}`),
  getCostBreakdown: (projectId) => api.get(`/projects/${projectId}/cost-breakdown`),

  // Bid Sheets
  createBidSheet: (data) => api.post('/bid-sheets', data),
  getBidSheets: (userId) => api.get('/bid-sheets', { params: { user_id: userId } }),
  getBidSheet: (bidSheetId) => api.get(`/bid-sheets/${bidSheetId}`),
  updateBidSheet: (bidSheetId, data) => api.patch(`/bid-sheets/${bidSheetId}`, data),
  deleteBidSheet: (bidSheetId) => api.delete(`/bid-sheets/${bidSheetId}`),
  createBidSheetItem: (bidSheetId, data) => api.post(`/bid-sheets/${bidSheetId}/items`, data),
  getBidSheetItems: (bidSheetId) => api.get(`/bid-sheets/${bidSheetId}/items`),
  updateBidSheetItem: (itemId, data) => api.patch(`/bid-sheet/items/${itemId}`, data),
  deleteBidSheetItem: (itemId) => api.delete(`/bid-sheet/items/${itemId}`),
  convertBidSheetToProject: (bidSheetId) => api.post(`/bid-sheets/${bidSheetId}/convert-to-project`),
  exportBidSheetCSV: (bidSheetId) => api.get(`/bid-sheets/${bidSheetId}/export-csv`, { responseType: 'blob' }),
  exportBidSheetPDF: (bidSheetId) => api.get(`/bid-sheets/${bidSheetId}/export-pdf`, { responseType: 'blob' }),
  // Bank Workflow
  submitBidSheetToBank: (bidSheetId, userId, bankEmail = null, loanOfficerName = null, customMessage = null) => {
    const params = { user_id: userId };
    if (bankEmail) params.bank_email = bankEmail;
    if (loanOfficerName) params.loan_officer_name = loanOfficerName;
    if (customMessage) params.custom_message = customMessage;
    return api.post(`/bid-sheets/${bidSheetId}/submit-to-bank`, null, { params });
  },
  markBidSheetApproved: (bidSheetId, userId, approvedAmount, bankNotes) => api.post(`/bid-sheets/${bidSheetId}/mark-approved`, null, { 
    params: { user_id: userId, approved_amount: approvedAmount, bank_notes: bankNotes } 
  }),
  awardContractorsFromBidSheet: (bidSheetId, userId, contractorIds, sendEmails = true) => api.post(`/bid-sheets/${bidSheetId}/award-contractors`, null, { 
    params: { user_id: userId, contractor_ids: contractorIds, send_emails: sendEmails } 
  }),
  importBidToBidSheet: (bidSheetId, bidRequestId, responseId, userId, additionalData = {}) => {
    // Build params without the response_id key when null — otherwise axios
    // sends `response_id=null` (literal string) which the backend treats as
    // truthy and then fails to find the response.
    const params = { bid_request_id: bidRequestId, user_id: userId };
    if (responseId) params.response_id = responseId;
    return api.post(`/bid-sheets/${bidSheetId}/import-from-bid`, additionalData, { params });
  },

  // v2.5.5.5: trade → stage breakdown map (single source of truth on backend)
  getTradeStages: () => api.get('/trade-stages'),
  exportBidSheetToBankFormat: (bidSheetId) => api.get(`/bid-sheets/${bidSheetId}/export-bank-format`, { responseType: 'blob' }),

  // Project Export
  exportProjectPDF: (projectId) => api.get(`/projects/${projectId}/export-pdf`, { responseType: 'blob' }),

  // Subscription
  getSubscriptionTiers: () => api.get('/subscription/tiers'),
  getUserSubscription: (userId) => api.get(`/subscription/user/${userId}`),
  startTrial: (userId, tierId) => api.post('/subscription/start-trial', null, { params: { user_id: userId, tier_id: tierId } }),
  createCheckoutSession: (data) => api.post('/subscription/checkout', data),
  getCheckoutStatus: (sessionId, userId) => api.get(`/subscription/checkout/status/${sessionId}`, { params: { user_id: userId } }),
  cancelSubscription: (userId) => api.post(`/subscription/cancel/${userId}`),

  // AI Voice Chatbot
  voiceChat: (data) => api.post('/voice-chat', data),
  textChat: (data) => api.post('/text-chat', data),
  transcribeAudio: (formData) => api.post('/transcribe-audio', formData),

  // Bid Requests
  createBidRequest: (userId, data) => api.post('/bid-requests', data, { params: { user_id: userId } }),
  getBidRequests: (userId, projectId, status) => api.get('/bid-requests', { 
    params: { user_id: userId, project_id: projectId, status } 
  }),
  getBidRequest: (bidRequestId, userId) => api.get(`/bid-requests/${bidRequestId}`, { params: { user_id: userId } }),
  updateBidRequest: (bidRequestId, userId, data) => api.patch(`/bid-requests/${bidRequestId}`, data, { params: { user_id: userId } }),
  updateBidResponse: (responseId, data, userId) => api.patch(`/bid-responses/${responseId}`, data, { params: { user_id: userId } }),
  deleteBidRequest: (bidRequestId, userId) => api.delete(`/bid-requests/${bidRequestId}`, { params: { user_id: userId } }),
  sendBidRequestToMoreContractors: (bidRequestId, userId, contractorIds) => api.post(
    `/bid-requests/${bidRequestId}/send-to-contractors`,
    { contractor_ids: contractorIds },
    { params: { user_id: userId } }
  ),
  awardBid: (bidRequestId, awardData) => api.post(`/bid-requests/${bidRequestId}/award`, awardData),
  getBidResponses: (bidRequestId, userId) => api.get(`/bid-requests/${bidRequestId}/responses`, { params: { user_id: userId } }),
  createManualBid: (bidRequestId, userId, data) => api.post(`/bid-requests/${bidRequestId}/manual-bid`, data, { params: { user_id: userId } }),
  createPhasesFromBid: (bidRequestId, responseId, userId, phaseSelections) => api.post(
    `/bid-requests/${bidRequestId}/create-phases`, 
    phaseSelections, 
    { params: { response_id: responseId, user_id: userId } }
  ),

  // Contractor Portal - Bid Requests
  getContractorBidRequests: (contractorEmail) => api.get('/contractor-portal/bid-requests', { 
    params: { contractor_email: contractorEmail } 
  }),
  submitBidResponse: (contractorEmail, data) => api.post('/contractor-portal/bid-responses', data, { 
    params: { contractor_email: contractorEmail } 
  }),
  downloadScopeAttachment: (bidRequestId, attachmentId) =>
    api.get(`/contractor-portal/bid-requests/${bidRequestId}/scope-attachments/${attachmentId}/download`, { responseType: 'blob' }),

  // Schedule System
  getScheduleTasks: (projectId, userId, phaseId) => api.get(`/schedule/tasks/${projectId}`, { 
    params: { user_id: userId, phase_id: phaseId } 
  }),
  createScheduleTask: (projectId, userId, data) => api.post('/schedule/tasks', data, { 
    params: { user_id: userId, project_id: projectId } 
  }),
  updateScheduleTask: (taskId, userId, data) => api.patch(`/schedule/tasks/${taskId}`, data, { 
    params: { user_id: userId } 
  }),
  deleteScheduleTask: (taskId, userId) => api.delete(`/schedule/tasks/${taskId}`, { 
    params: { user_id: userId } 
  }),
  importSchedule: (projectId, userId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    formData.append('user_id', userId);
    formData.append('preview_only', 'true');
    return api.post('/schedule/import', formData);
  },
  applyScheduleChanges: (projectId, userId, changes) => api.post(`/schedule/apply-changes/${projectId}`, { changes }, {
    params: { user_id: userId }
  }),
  exportSchedulePdf: (projectId, userId) => api.get(`/project/${projectId}/schedule/export-pdf`, {
    params: { user_id: userId },
    responseType: 'blob'
  }),
  exportScheduleExcel: (projectId, userId) => api.get(`/project/${projectId}/schedule/export-excel`, {
    params: { user_id: userId },
    responseType: 'blob'
  }),
  sendScheduleToContractor: (projectId, phaseId, contractorId, userId) => api.post('/schedule/send-to-contractor', null, {
    params: { project_id: projectId, phase_id: phaseId, contractor_id: contractorId, user_id: userId }
  }),
  // Option 3: Send selected schedule items to contractor
  sendScheduleItemsToContractor: (projectId, contractorId, taskIds, userId, message = null) => api.post('/schedule/send-items-to-contractor', {
    project_id: projectId,
    contractor_id: contractorId,
    task_ids: taskIds,
    message: message
  }, {
    params: { user_id: userId }
  }),
  getScheduleApprovals: (projectId, userId) => api.get(`/schedule/approvals/${projectId}`, { 
    params: { user_id: userId } 
  }),
  
  // Schedule Messages
  sendScheduleMessage: (userId, data) => api.post('/schedule/messages', data, { params: { user_id: userId } }),
  getScheduleMessages: (projectId, userId, phaseId) => api.get(`/schedule/messages/${projectId}`, { 
    params: { user_id: userId, phase_id: phaseId } 
  }),

  // AI Health Analysis
  getAIHealthAnalysis: (projectId, userId) => api.get(`/project/${projectId}/ai-health`, { 
    params: { user_id: userId } 
  }),
  getCashFlowForecast: (projectId, userId) => api.get(`/project/${projectId}/cash-flow-forecast`, { 
    params: { user_id: userId } 
  }),
  getContractorPerformance: (contractorId, userId) => api.get(`/contractors/${contractorId}/performance`, { 
    params: { user_id: userId } 
  }),
  getProjectAlerts: (projectId, userId, includeDismissed = false) => api.get(`/project/${projectId}/alerts`, { 
    params: { user_id: userId, include_dismissed: includeDismissed } 
  }),
  updateAlert: (alertId, userId, data) => api.patch(`/alerts/${alertId}`, data, { params: { user_id: userId } }),
  getWeatherForecast: (projectId, userId) => api.get(`/project/${projectId}/weather-forecast`, { 
    params: { user_id: userId } 
  }),

  // Contractor Portal - Schedule
  getContractorSchedule: (email) => api.get('/contractor-portal/schedule', { params: { email } }),
  respondToSchedule: (email, phaseId, approvalId, data) => api.post('/contractor-portal/schedule/respond', data, { 
    params: { email, phase_id: phaseId, approval_id: approvalId } 
  }),
  getContractorMessages: (email, projectId, bidRequestId) => api.get('/contractor-portal/messages', { 
    params: { email, project_id: projectId, bid_request_id: bidRequestId } 
  }),
  sendContractorMessage: (email, message, projectId, bidRequestId, phaseId, subject) => api.post('/contractor-portal/messages', null, { 
    params: { email, message, project_id: projectId, bid_request_id: bidRequestId, phase_id: phaseId, subject } 
  }),

  // User-to-Contractor Messaging (Full Chat System)
  sendMessage: (userId, contractorId, message, projectId, bidRequestId, subject) => api.post('/messages/send', null, {
    params: { user_id: userId, contractor_id: contractorId, message, project_id: projectId, bid_request_id: bidRequestId, subject }
  }),
  getMessageThread: (userId, contractorId, bidRequestId, projectId) => api.get('/messages/thread', {
    params: { user_id: userId, contractor_id: contractorId, bid_request_id: bidRequestId, project_id: projectId }
  }),
  getUnreadMessageCount: (userId) => api.get('/messages/unread-count', { params: { user_id: userId } }),
  markMessagesRead: (userId, contractorId, bidRequestId) => api.post('/messages/mark-read', null, {
    params: { user_id: userId, contractor_id: contractorId, bid_request_id: bidRequestId }
  }),
  // Full conversation management
  getConversations: (userId) => api.get('/messages/conversations', { params: { user_id: userId } }),
  getConversation: (userId, contractorId) => api.get(`/messages/conversation/${contractorId}`, { params: { user_id: userId } }),
  getContactableContractors: (userId) => api.get('/messages/contactable-contractors', { params: { user_id: userId } }),

  // Contractor messaging
  contractorGetConversations: (email) => api.get('/contractor-portal/conversations', { params: { email } }),
  contractorMarkRead: (email, userId) => api.post('/contractor-portal/messages/mark-read', null, { params: { email, user_id: userId } }),

  // Revoke and Transfer Award
  revokeAward: (bidRequestId, userId, reason, notifyContractor = true) => api.post(`/bid-requests/${bidRequestId}/revoke-award`, {
    user_id: userId, reason, notify_contractor: notifyContractor
  }),
  transferAward: (bidRequestId, userId, newResponseId, contractFileId, notifyOld = true) => api.post(`/bid-requests/${bidRequestId}/transfer-award`, {
    user_id: userId, new_response_id: newResponseId, contract_file_id: contractFileId, notify_old_contractor: notifyOld
  }),

  // Quick Bid Invite
  sendQuickBidInvite: (userId, data) => api.post('/quick-bid-invite', data, { params: { user_id: userId } }),
  getQuickBidInvite: (token) => api.get(`/quick-bid-invite/${token}`),
  submitQuickBid: (token, file, notes = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (notes) formData.append('notes', notes);
    return api.post(`/quick-bid-invite/${token}/submit`, formData, {
            timeout: 120000
    });
  },
  getQuickBidInvites: (userId, projectId = null) => api.get('/quick-bid-invites', { 
    params: { user_id: userId, project_id: projectId } 
  }),

  // User Alerts/Notifications
  getAllAlerts: (userId, includeDismissed = false, limit = 20) => api.get('/alerts', {
    params: { user_id: userId, include_dismissed: includeDismissed, limit }
  }),
  dismissAlert: (alertId, userId) => api.patch(`/alerts/${alertId}`, { is_dismissed: true }, {
    params: { user_id: userId }
  }),
  markAlertRead: (alertId, userId) => api.patch(`/alerts/${alertId}`, { is_read: true }, {
    params: { user_id: userId }
  }),

  // Bid Revision Requests
  requestBidRevision: (userId, data) => api.post('/bid-revision-request', data, { params: { user_id: userId } }),
  getBidRevision: (token) => api.get(`/bid-revision/${token}`),
  submitBidRevision: (token, data) => api.post(`/bid-revision/${token}/submit`, data),
  
  // Bid Reminders
  sendBidReminder: (bidRequestId, contractorId, userId) => api.post(`/bid-requests/${bidRequestId}/remind/${contractorId}`, null, { params: { user_id: userId } }),
  sendBidRequestReminders: (bidRequestId, userId) => api.post(`/bid-requests/${bidRequestId}/remind-all`, null, { params: { user_id: userId } }),
  sendQuickBidReminder: (inviteId, userId) => api.post(`/quick-bid-invites/${inviteId}/remind`, null, { params: { user_id: userId } }),

  // Subscription & Project Limits
  getSubscriptionStatus: (userId) => api.get(`/subscription/status/${userId}`),
  canCreateProject: (userId) => api.get(`/subscription/can-create-project/${userId}`),
  createSetupIntent: (userId) => api.post(`/subscription/create-setup-intent/${userId}`),
  updatePaymentMethod: (userId, paymentMethodId, tier) => api.post(`/subscription/update-payment-method/${userId}`, { payment_method_id: paymentMethodId, tier: tier || 'basic' }),
  submitEnterpriseContact: (data) => api.post('/enterprise/contact-request', data),
  extendAllTrials: (adminUserId, days) => api.post('/admin/extend-all-trials', null, { params: { admin_user_id: adminUserId, days } }),

  // Beta / Admin
  getBetaStatus: () => api.get('/beta/status'),
  getBetaStats: () => api.get('/admin/beta/stats'),
  getAllUsers: () => api.get('/admin/users'),
  approveUser: (userId) => api.patch(`/admin/users/${userId}/approve`),
  denyUser: (userId) => api.patch(`/admin/users/${userId}/deny`),
  makeAdmin: (userId) => api.patch(`/admin/users/${userId}/make-admin`),
  createInviteCode: (uses = 1, expiresDays = null) => api.post('/admin/invite-codes', null, { params: { uses, expires_days: expiresDays } }),
  getInviteCodes: () => api.get('/admin/invite-codes'),
  deleteInviteCode: (code) => api.delete(`/admin/invite-codes/${code}`),

  // ============ CONTRACTOR PORTAL V2 ============
  
  // Get all portal data for a contractor (multi-phase view)
  getPortalV2Data: (token) => api.get('/portal/v2/data', { params: { token } }),
  
  // Submit an invoice from contractor portal
  submitPortalInvoice: (token, formData) => api.post('/portal/v2/invoices/submit', formData, {
    params: { token },
        timeout: 60000
  }),
  
  // Get contractor's invoices
  getPortalInvoices: (token) => api.get('/portal/v2/invoices', { params: { token } }),
  
  // Owner review endpoints
  reviewInvoice: (invoiceId, userId, action, rejectionReason = null) => api.post(`/invoices/${invoiceId}/review`, 
    { action, rejection_reason: rejectionReason }, 
    { params: { user_id: userId } }
  ),
  
  // Get pending reviews for owner
  getPendingReviews: (userId) => api.get('/owner/pending-reviews', { params: { user_id: userId } }),
  
  // Review W9/COI documents
  reviewContractorW9: (contractorId, userId, action, rejectionReason = null) => api.post(`/contractors/${contractorId}/w9/review`,
    { action, rejection_reason: rejectionReason },
    { params: { user_id: userId } }
  ),
  reviewContractorCOI: (contractorId, userId, action, rejectionReason = null) => api.post(`/contractors/${contractorId}/coi/review`,
    { action, rejection_reason: rejectionReason },
    { params: { user_id: userId } }
  ),
  
  // Owner alerts/notifications
  getOwnerAlerts: (userId, includeDismissed = false) => api.get('/owner/alerts', { 
    params: { user_id: userId, include_dismissed: includeDismissed } 
  }),
  markOwnerAlertRead: (alertId, userId) => api.post(`/owner/alerts/${alertId}/read`, null, { params: { user_id: userId } }),
  dismissOwnerAlert: (alertId, userId) => api.post(`/owner/alerts/${alertId}/dismiss`, null, { params: { user_id: userId } }),
  
  // Admin portal management
  resendAllPortalEmails: (userId) => api.post('/admin/resend-portal-emails', null, { params: { user_id: userId } }),
  migratePhaseTokens: (userId) => api.post('/admin/migrate-phase-tokens', null, { params: { user_id: userId } }),
  
  // Resend portal email to specific contractor
  resendContractorPortalEmail: (contractorId, userId) => api.post(`/contractors/${contractorId}/resend-portal-email`, null, { params: { user_id: userId } }),
  
  // Resubmit rejected invoice
  resubmitInvoice: (invoiceId, token, formData) => api.post(`/portal/v2/invoices/${invoiceId}/resubmit`, formData, {
    params: { token },
        timeout: 60000
  }),

  // ============ CONTRACTOR PORTAL V2 - AUTH, MESSAGING, BIDS ============
  
  // Contractor auth
  contractorSetPassword: (token, password) => api.post('/portal/v2/auth/set-password', { token, password }),
  contractorLogin: (email, password) => api.post('/portal/v2/auth/login', { email, password }),
  contractorForgotPassword: (email) => api.post('/portal/v2/auth/forgot-password', { email }),
  contractorResetPassword: (token, newPassword) => api.post('/portal/v2/auth/reset-password', { token, new_password: newPassword }),
  
  // Contractor portal - open bids
  getContractorOpenBids: (token) => api.get('/portal/v2/open-bids', { params: { token } }),
  submitBidFromPortal: (token, formData) => api.post('/portal/v2/bids/submit', formData, {
    params: { token },
        timeout: 60000
  }),
  
  // Contractor portal - messaging
  getContractorPortalMessages: (token, projectId = null) => api.get('/portal/v2/messages', { 
    params: { token, project_id: projectId } 
  }),
  sendContractorPortalMessage: (token, content, projectId = null) => api.post('/portal/v2/messages/send', 
    { content, project_id: projectId }, 
    { params: { token } }
  ),
  
  // Contractor logo upload
  updateContractorLogo: (token, logoBase64) => api.post('/portal/v2/logo', 
    { logo: logoBase64 }, 
    { params: { token } }
  ),
  
  // ============ CONTRACTOR SELF-SIGNUP ============
  
  // Get signup page info (public)
  getContractorSignupInfo: (signupCode) => api.get(`/contractor-signup/${signupCode}/info`),
  
  // Register new contractor (public)
  registerContractor: (signupCode, data) => api.post(`/contractor-signup/${signupCode}/register`, data),
  
  // Upload W-9 during contractor portal signup (uses base64 encoding)
  uploadContractorW9Portal: (token, fileBase64, fileName) => api.post('/portal/v2/w9', 
    { file: fileBase64, filename: fileName }, 
    { params: { token } }
  ),

  // v2.5.12 — Upload COI via the contractor portal (token-authenticated,
  // base64 payload, mandatory expiration_date). Mirrors uploadContractorW9Portal.
  uploadContractorCOIPortal: (token, fileBase64, fileName, expirationDate) => api.post(
    '/portal/v2/coi',
    { file: fileBase64, filename: fileName, expiration_date: expirationDate },
    { params: { token } },
  ),
  
  // Get user's contractor signup code/QR link
  getContractorSignupCode: (userId) => api.get(`/users/${userId}/contractor-signup-code`),
  
  // Regenerate signup code
  regenerateContractorSignupCode: (userId) => api.post(`/users/${userId}/contractor-signup-code/regenerate`),
  
  // Send contractor invite email
  sendContractorInviteEmail: (userId, data) => api.post(`/users/${userId}/send-contractor-invite`, data),

  // ============ HOLDING COSTS ============
  
  getHoldingCostCategories: () => api.get('/holding-cost-categories'),
  getHoldingCosts: (projectId) => api.get(`/projects/${projectId}/holding-costs`),
  createHoldingCost: (projectId, data) => api.post(`/projects/${projectId}/holding-costs`, data),
  updateHoldingCost: (projectId, costId, data) => api.patch(`/projects/${projectId}/holding-costs/${costId}`, data),
  deleteHoldingCost: (projectId, costId) => api.delete(`/projects/${projectId}/holding-costs/${costId}`),
  
  // Holding cost recurring entries
  addHoldingCostEntry: (projectId, costId, data) => api.post(`/projects/${projectId}/holding-costs/${costId}/entries`, data),
  updateHoldingCostEntry: (projectId, costId, entryId, data) => api.patch(`/projects/${projectId}/holding-costs/${costId}/entries/${entryId}`, data),
  deleteHoldingCostEntry: (projectId, costId, entryId) => api.delete(`/projects/${projectId}/holding-costs/${costId}/entries/${entryId}`),
  
  // Holding cost receipts
  uploadHoldingCostReceipt: (projectId, costId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/holding-costs/${costId}/receipt`, formData);
  },
  uploadHoldingCostEntryReceipt: (projectId, costId, entryId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/holding-costs/${costId}/entries/${entryId}/receipt`, formData);
  },
  downloadHoldingCostReceipt: (projectId, costId) => api.get(`/projects/${projectId}/holding-costs/${costId}/receipt`, { responseType: 'blob' }),

  // ============ PHASE RECEIPT UPLOAD ============
  
  uploadPhaseReceiptFile: (phaseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/phases/${phaseId}/receipt-upload`, formData);
  },
  downloadPhaseReceiptFile: (phaseId) => api.get(`/phases/${phaseId}/receipt-download`, { responseType: 'blob' }),
  deletePhaseReceiptFile: (phaseId) => api.delete(`/phases/${phaseId}/receipt-delete`),

  // ============ TIMEZONE ============
  
  detectTimezone: (address) => api.post('/detect-timezone', { address }),
  getTimezones: () => api.get('/timezones'),
};